import React, { useEffect, useMemo, useState } from 'react';
import { financeiroAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { ArrowRightLeft } from 'lucide-react';
import { toast } from 'sonner';
import ManejoGrid, { MANEJO_KEYS, manejoZerado, totalManejo } from './ManejoGrid';

/**
 * Modal de transferência de manejo entre dois serviços.
 * - lista serviços destino: em andamento + gerir_manejo=true (exceto a origem)
 * - usa ManejoGrid com `max` = manejo atual da origem
 */
export default function ManejoTransferModal({ origem, onClose, onTransferido }) {
  const [destinos, setDestinos] = useState([]);
  const [destinoId, setDestinoId] = useState('');
  const [quantidades, setQuantidades] = useState(manejoZerado());
  const [loading, setLoading] = useState(false);
  const [destinoData, setDestinoData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await financeiroAPI.listServices({ em_andamento: true });
        const list = (res.data || []).filter(s => s.id !== origem.id && s.gerir_manejo);
        setDestinos(list);
      } catch (e) {
        toast.error('Erro ao listar serviços destino');
      }
    })();
  }, [origem.id]);

  useEffect(() => {
    const d = destinos.find(s => s.id === destinoId);
    setDestinoData(d || null);
  }, [destinoId, destinos]);

  const totalTransf = useMemo(() => totalManejo(quantidades), [quantidades]);

  const submit = async () => {
    if (!destinoId) { toast.error('Selecione o serviço destino'); return; }
    if (totalTransf === 0) { toast.error('Informe ao menos uma quantidade > 0'); return; }
    setLoading(true);
    try {
      // remover chaves zeradas
      const qSend = {};
      for (const k of MANEJO_KEYS) {
        if ((quantidades[k] || 0) > 0) qSend[k] = quantidades[k];
      }
      await financeiroAPI.transferirManejo(origem.id, {
        destino_servico_id: destinoId,
        quantidades: qSend,
      });
      toast.success(`Transferidas ${totalTransf} unidade(s)`);
      onTransferido && onTransferido();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao transferir');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="dialog-transferir-manejo">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="w-5 h-5 text-cyan-600" />
            Transferir Manejo
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Origem */}
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <p className="text-[10px] uppercase font-semibold text-emerald-700 tracking-wider">Serviço Origem</p>
            <p className="font-medium">{origem.titulo}</p>
            <p className="text-xs text-muted-foreground">{origem.cliente_nome} · Total atual: <strong>{totalManejo(origem.manejo)}</strong></p>
          </div>

          {/* Destino */}
          <div>
            <Label>Serviço Destino *</Label>
            <Select value={destinoId} onValueChange={setDestinoId}>
              <SelectTrigger data-testid="select-destino"><SelectValue placeholder="Selecione um serviço em andamento com 'Gerir Manejo'" /></SelectTrigger>
              <SelectContent>
                {destinos.length === 0 ? (
                  <div className="p-3 text-xs text-muted-foreground text-center">
                    Nenhum outro serviço em andamento com manejo habilitado. Crie um destino primeiro.
                  </div>
                ) : destinos.map(d => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.titulo} — {d.cliente_nome} · {totalManejo(d.manejo)} no destino
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Grid de quantidades a transferir */}
          {destinoId && (
            <div>
              <p className="text-sm font-semibold mb-2">Quantidades a transferir</p>
              <p className="text-[11px] text-muted-foreground mb-2">
                O limite "de X" abaixo de cada célula é o que existe atualmente no serviço origem.
              </p>
              <ManejoGrid
                value={quantidades}
                onChange={setQuantidades}
                max={origem.manejo || manejoZerado()}
                label="A transferir"
              />
              {destinoData && (
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg">
                    <p className="text-[10px] uppercase font-semibold text-rose-700">Origem ficará com</p>
                    <p className="text-2xl font-bold text-rose-700">{totalManejo(origem.manejo) - totalTransf}</p>
                  </div>
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <p className="text-[10px] uppercase font-semibold text-emerald-700">Destino ficará com</p>
                    <p className="text-2xl font-bold text-emerald-700">{totalManejo(destinoData.manejo) + totalTransf}</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={loading || !destinoId || totalTransf === 0} data-testid="btn-confirmar-transferencia">
            <ArrowRightLeft className="w-4 h-4 mr-2" />
            {loading ? 'Transferindo...' : `Transferir ${totalTransf} unidade(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
